import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import AINotes from "./pages/AINotes";
import TreatmentPlans from "./pages/TreatmentPlans";
import PriorAuth from "./pages/PriorAuth";
import Intake from "./pages/Intake";
import Billing from "./pages/Billing";
import Insurance from "./pages/Insurance";
import Scheduling from "./pages/Scheduling";
import Medications from "./pages/Medications";
import SafetyPlans from "./pages/SafetyPlans";
import Referrals from "./pages/Referrals";
import Outcomes from "./pages/Outcomes";
import Telehealth from "./pages/Telehealth";
import EAP from "./pages/EAP";
import Supervision from "./pages/Supervision";
import Marketing from "./pages/Marketing";
import Settings from "./pages/Settings";
import Documents from "./pages/Documents";
import Merge from "./pages/Merge";
import Forms from "./pages/Forms";
import Privacy from "./pages/Privacy";
import AuditLog from "./pages/AuditLog";
import MFASetup from "./pages/MFASetup";
import ReferralRewards from "./pages/ReferralRewards";
import TempAccess from "./pages/TempAccess";
import AdminStats from "./pages/AdminStats";
import SiteEditor from "./pages/SiteEditor";
import Messages from "./pages/Messages";
import PortalHome from "./pages/portal/PortalHome";
import PaymentSuccess from "./pages/portal/PaymentSuccess";
import { PortalLogin, PortalAccept, PortalForgot, PortalReset } from "./pages/portal/PortalAuth";
import { SiteGate } from "./components/SiteGate";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center"><div className="mf-skeleton w-40 h-10"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Protected>{children}</Protected>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <SiteGate>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/notes" element={<Protected><AINotes /></Protected>} />
            <Route path="/treatment-plans" element={<Protected><TreatmentPlans /></Protected>} />
            <Route path="/prior-auth" element={<Protected><PriorAuth /></Protected>} />
            <Route path="/intake" element={<Protected><Intake /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/insurance" element={<Protected><Insurance /></Protected>} />
            <Route path="/scheduling" element={<Protected><Scheduling /></Protected>} />
            <Route path="/medications" element={<Protected><Medications /></Protected>} />
            <Route path="/safety-plans" element={<Protected><SafetyPlans /></Protected>} />
            <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
            <Route path="/outcomes" element={<Protected><Outcomes /></Protected>} />
            <Route path="/telehealth" element={<Protected><Telehealth /></Protected>} />
            <Route path="/eap" element={<Protected><EAP /></Protected>} />
            <Route path="/supervision" element={<Protected><Supervision /></Protected>} />
            <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/merge" element={<Protected><Merge /></Protected>} />
            <Route path="/forms" element={<Protected><Forms /></Protected>} />
            <Route path="/privacy" element={<Protected><Privacy /></Protected>} />
            <Route path="/audit-log" element={<Protected><AuditLog /></Protected>} />
            <Route path="/mfa" element={<Protected><MFASetup /></Protected>} />
            <Route path="/referral-rewards" element={<Protected><ReferralRewards /></Protected>} />
            <Route path="/admin/stats" element={<AdminOnly><AdminStats /></AdminOnly>} />
            <Route path="/admin/site" element={<AdminOnly><SiteEditor /></AdminOnly>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/portal" element={<PortalHome />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/accept" element={<PortalAccept />} />
            <Route path="/portal/forgot" element={<PortalForgot />} />
            <Route path="/portal/reset" element={<PortalReset />} />
            <Route path="/portal/payment/success" element={<PaymentSuccess />} />
            <Route path="/admin/temp-access" element={<AdminOnly><TempAccess /></AdminOnly>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </SiteGate>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
